import PromptComponent from "slotworks/components/prompt/prompt-component";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import { default as FlexiToggle, ToggleEvent } from "slotworks/graphics/elements/flexi-toggle";
import Layers from "slotworks/graphics/layers/layers";
import Scene from "slotworks/graphics/layers/scene";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import Currency from "slotworks/i18n/currency";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import { parallel } from "slotworks/utils/race-utils";
import * as TweenJS from "slotworks/utils/tween";
import { log } from "util";

export enum LoadType {
    DISABLE_BUTTON = "disableButton",
    LOADING_SCENE = "loadingScene",
    HIDE_BUTTON = "hideButton"
}

export default class PromptContent {

    public timeOutTime: number;
    public continueButton: FlexiButton;

    private loadRaceType: LoadType = LoadType.DISABLE_BUTTON;

    private buttons = new Map<FlexiButton, () => void>();
    private toggles = new Map<FlexiToggle, (value: boolean) => void>();
    private scene: Scene;

    private continueButtonName: string;
    private textContents: Map<string, string>;
    private buttonContents = new Map<string, () => void>();
    private toggleContents = new Map<string, (value: boolean) => void>();
    private countups = new Map<string, CountupDefinition>();

    private spinnerTween: TweenJS.Tween;

    private continueHandler: Function;

    constructor(private scenes: string[], time?: number) {
        if (time == null) {
            this.timeOutTime = PromptComponent.DEFAULT_TIME;
        } else {
            this.timeOutTime = time;
        }

        this.scene = Layers.Prompts.getFirstValidScene(scenes);

        if (!this.scene) {
            throw new Error("No scene found in entire list for prompt. [" + scenes.join(", ") + "]");
        }
    }

    public show(): Contract<void> {

        const setupContents = () => {
            if (this.textContents) {
                this.textContents.forEach((value, index, map) => {
                    this.setText(index, value);
                });
            }

            if (this.countups) {
                this.countups.forEach((countup: CountupDefinition, contentName: string) => {
                    this.setText(contentName, Currency.format(0, undefined, undefined, true));
                });
            }

            if (this.continueButtonName) {
                this.continueButton = this.setButton(this.continueButtonName);
            }
        };

        return new Contract<void>((resolve) => {
            Layers.Prompts.setScene(this.scene, setupContents).then(() => {
                if (this.buttonContents.size > 0) {

                    // Clean Button Contents Before Starting render
                    try {
                        const buttonsDiv = document.querySelector("#message.activeScene #errorBox #buttons");
                        buttonsDiv.innerHTML = "";
                    } catch (e) {
                        log(e);
                    }


                    this.buttonContents.forEach((value, index, map) => {
                        this.setButton(index, value);
                    });
                }

                if (this.toggleContents.size > 0) {
                    this.toggleContents.forEach((value, index, map) => {
                        this.setToggle(index, value);
                    });
                }

                if (this.countups.size > 0) {
                    const countupContracts: Array<() => Contract<void>> = [];

                    this.countups.forEach((countup: CountupDefinition, contentName: string) => {
                        countupContracts.push(() => this.startCountup(countup, contentName));
                    });

                    parallel(countupContracts).then(() => {
                        this.enableButtons();
                    });
                } else {
                    this.enableButtons();
                }

                if (Layers.Prompts.getSprite("spinner")) {
                    this.spinnerTween = new TweenJS.Tween(CenterPivot(Layers.Prompts.getSprite("spinner")))
                        .to({ rotation: Math.PI * 2 }, 500)
                        .repeat(Infinity)
                        .start();
                }

                resolve(null);
            });
        });
    }

    public addText(contents: Map<string, string>) {
        this.textContents = contents;
    }

    public addContinueButton(name: string = "continue"): void {
        this.continueButtonName = name;
    }

    public hasContinueButton() {
        return this.continueButton;
    }

    public addContinueListener(upHandler: () => void) {
        if (this.hasContinueButton()) {
            this.continueHandler = () => {
                SoundManager.event(SoundEvent.prompt_continue_press);
                this.continueButton.setEnabled(false);
                upHandler();
            };
            this.continueButton.on(ButtonEvent.POINTER_DOWN, this.continueHandler);
        }
    }

    public hideContinueButton() {
        if (this.hasContinueButton()) {
            this.continueButton.setVisible(false);
        }
    }

    public disableContinueButton() {
        if (this.hasContinueButton()) {
            this.continueButton.setEnabled(false);
        }
    }

    public enableContinueButton() {
        if (this.hasContinueButton()) {
            this.continueButton.setVisible(true);
            this.continueButton.setEnabled(true);
        }
    }

    public addButton(name: string, upHandler?: () => void) {
        this.buttonContents.set(name, upHandler);
    }

    public addToggle(name: string, toggleHandler: (value: boolean) => void) {
        this.toggleContents.set(name, toggleHandler);
    }

    public countupText(name: string, value: number, time: number) {
        this.countups.set(name, new CountupDefinition(value, time));
    }

    public hide(): Contract<void> {
        return new Contract<void>((resolve) => {

            this.destroy();

            Layers.Prompts.defaultScene().then(() => {
                resolve(null);
            });
        });
    }

    public destroy() {
        if (this.continueButton && this.continueHandler) {
            this.continueButton.off(ButtonEvent.POINTER_DOWN, this.continueHandler);
        }

        if (this.spinnerTween) {
            this.spinnerTween.stop();
            delete this.spinnerTween;
        }

        this.buttons.forEach((value, index, map) => {
            index.off(ButtonEvent.POINTER_UP, value);
            index.destroy();
        });

        this.toggles.forEach((value, index, map) => {
            index.off(ToggleEvent.CHANGE, value);
            index.destroy();
        });
    }

    public setLoadType(type: LoadType) {
        this.loadRaceType = type;
    }

    public getLoadRaceType(): LoadType {
        return this.loadRaceType;
    }

    private enableButtons() {
        this.buttons.forEach((value: any, button: FlexiButton) => {
            button.setEnabled(true);
        });
    }

    private startCountup(countup: CountupDefinition, contentName: string) {
        return new Contract<void>((resolve) => {
            if (countup.value > 0) {
                SoundManager.event(SoundEvent.prompt_countup_start);
                SoundManager.event(SoundEvent.prompt_countup_start_SCENE, this.scene.name);
            }

            const onComplete = () => {
                if (countup.value > 0) {
                    SoundManager.event(SoundEvent.prompt_countup_end);
                    SoundManager.event(SoundEvent.prompt_countup_end_SCENE, this.scene.name);
                }

                this.setText(contentName, Currency.format(countup.value, undefined, undefined, true));
                resolve(null);
                document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getEventString(), skipCountup, true);
            };

            const tickObj = { value: 0 };
            const tween = new TweenJS.Tween(tickObj)
                .to({ value: countup.value }, countup.time)
                .onUpdate(() => {
                    this.setText(contentName, Currency.format(tickObj.value, undefined, undefined, true, true));
                })
                .onComplete(onComplete);

            const skipCountup = () => {
                tween.stop();
                onComplete();
            };

            document.body.addEventListener(ButtonEvent.POINTER_DOWN.getEventString(), skipCountup, true);

            tween.start();
        });
    }

    private setButton(name: string, upHandler?: () => void): FlexiButton {
        const button = Layers.Prompts.getFlexiButton(name);
        if (button) {
            if (upHandler) {
                const scopedUpHandler = () => upHandler();
                button.on(ButtonEvent.POINTER_UP, scopedUpHandler);
                this.buttons.set(button, scopedUpHandler);
            }

            button.setEnabled(false);
        }

        return button;
    }

    private setToggle(name: string, toggleHandler: (value: boolean) => void) {
        const toggle = Layers.Prompts.getFlexiToggle(name);
        if (toggle) {
            toggle.setValue(false);

            const handler = () => {
                const toggleValue = toggle.getValue();
                toggleHandler(toggleValue);
            };

            toggle.on(ToggleEvent.CHANGE, handler);

            this.toggles.set(toggle, handler);
        }
    }

    private setText(id: string, text: string) {
        const textField = Layers.Prompts.getFlexiText(id);

        if (textField) {
            textField.setText(text);
        }
    }
}

class CountupDefinition {
    constructor(public value: number, public time: number) {
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/components/prompt/prompt-content.ts